/**
 * Project data — DESIGN.md §6.1, §6.14.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  CreateProjectRequest,
  CreateProjectStatusUpdateRequest,
  CreateSectionRequest,
  Page,
  Project,
  ProjectAccess,
  ProjectStatusUpdate,
  ProjectWithSections,
  Section,
  UpdateProjectAccessRequest,
  UpdateProjectRequest,
} from '@slate/shared';
import { http } from '@/lib/api';
import { queryKeys } from '@/lib/keys';

export function useProjects(
  status: 'active' | 'archived' | 'all' = 'active',
  q: string | null = null,
): UseQueryResult<Page<Project>, Error> {
  return useQuery({
    queryKey: queryKeys.projectList(status, q),
    queryFn: () => http.get<Page<Project>>('/projects', { query: { status, q, limit: 100 } }),
    staleTime: 60_000,
  });
}

/**
 * The paginated form, for the projects page.
 *
 * `useProjects` fetches one page, which is right for a picker and wrong for
 * the directory: every list endpoint is cursor-paginated (§12.3), so a
 * hundred-and-first project would otherwise be invisible rather than merely
 * further down. Found the honest way — an E2E run accumulated 245 projects and
 * the newest one stopped appearing.
 */
export function useProjectsPaginated(
  status: 'active' | 'archived' | 'all' = 'active',
  q: string | null = null,
) {
  return useInfiniteQuery({
    queryKey: [...queryKeys.projectList(status, q), 'infinite'] as const,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      http.get<Page<Project>>('/projects', {
        query: { status, q, limit: 100, cursor: pageParam },
      }),
    getNextPageParam: (last) => last.cursor.next,
    staleTime: 60_000,
  });
}

/** Projects are addressed by key in the URL — §13.1 (`/projects/TLC`). */
export function useProject(key: string | null): UseQueryResult<ProjectWithSections, Error> {
  return useQuery({
    queryKey: queryKeys.project(key ?? 'none'),
    queryFn: () => http.get<ProjectWithSections>(`/projects/by-key/${key!}`),
    enabled: Boolean(key),
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProjectRequest) => http.post<ProjectWithSections>('/projects', input),
    onSuccess: async (project) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.projects });
      toast.success(`Created ${project.name}`);
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

export function useUpdateProject(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateProjectRequest) => http.patch<Project>(`/projects/${id}`, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.projects });
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/**
 * Who can see a private project — #9.
 *
 * Fetched only while the dialog is open: it is an administrative detail, not
 * something every project page should pay for.
 */
export function useProjectAccess(projectId: string | null): UseQueryResult<ProjectAccess, Error> {
  return useQuery({
    queryKey: queryKeys.projectAccess(projectId ?? 'none'),
    queryFn: () => http.get<ProjectAccess>(`/projects/${projectId!}/access`),
    enabled: Boolean(projectId),
  });
}

export function useUpdateProjectAccess(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateProjectAccessRequest) =>
      http.put<ProjectAccess>(`/projects/${projectId}/access`, input),
    onSuccess: async (access) => {
      queryClient.setQueryData(queryKeys.projectAccess(projectId), access);
      // The project list itself can change shape — someone just gained or lost
      // sight of it — so it is refetched rather than patched.
      await queryClient.invalidateQueries({ queryKey: queryKeys.projects });
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

export function useArchiveProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      http.post<Project>(`/projects/${id}/${archived ? 'archive' : 'unarchive'}`),
    onSuccess: async (project) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.projects });
      toast.success(project.status === 'archived' ? 'Project archived' : 'Project restored');
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/** §6.1 — deleting requires the project key typed back. */
export function useDeleteProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, confirmKey }: { id: string; confirmKey: string }) =>
      http.delete<void>(`/projects/${id}`, { confirmKey }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.projects });
      toast.success('Project deleted');
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

export function useSections(projectId: string | null): UseQueryResult<Section[], Error> {
  return useQuery({
    queryKey: queryKeys.projectSections(projectId ?? 'none'),
    queryFn: () => http.get<Section[]>(`/projects/${projectId!}/sections`),
    enabled: Boolean(projectId),
  });
}

export function useSectionMutations(projectId: string) {
  const queryClient = useQueryClient();
  const settle = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.projects }),
      // Cards carry their section's name and a delete moves them to "(No
      // section)" — the list is wrong until it refetches.
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks }),
    ]);
  };

  return {
    create: useMutation({
      mutationFn: (input: CreateSectionRequest) =>
        http.post<Section>(`/projects/${projectId}/sections`, input),
      onSuccess: settle,
      onError: (error: Error) => toast.error(error.message),
    }),
    rename: useMutation({
      mutationFn: ({ id, name }: { id: string; name: string }) =>
        http.patch<Section>(`/sections/${id}`, { name }),
      onSuccess: settle,
      onError: (error: Error) => toast.error(error.message),
    }),
    /**
     * Reorder — §6.3.1's fractional index, so moving a section writes one row.
     * The caller works out the key it lands between; this only ships it.
     */
    move: useMutation({
      mutationFn: ({ id, position }: { id: string; position: string }) =>
        http.patch<Section>(`/sections/${id}`, { position }),
      onSuccess: settle,
      onError: (error: Error) => toast.error(error.message),
    }),
    remove: useMutation({
      mutationFn: (id: string) => http.delete<void>(`/sections/${id}`),
      onSuccess: async () => {
        await settle();
        toast.success('Section deleted — its cards moved to “No section”');
      },
      onError: (error: Error) => toast.error(error.message),
    }),
  };
}

// ------------------------------------------------- status updates & follow

export function useStatusUpdates(
  projectId: string | null,
): UseQueryResult<ProjectStatusUpdate[], Error> {
  return useQuery({
    queryKey: queryKeys.projectStatusUpdates(projectId ?? 'none'),
    queryFn: () => http.get<ProjectStatusUpdate[]>(`/projects/${projectId!}/status-updates`),
    enabled: Boolean(projectId),
  });
}

export function usePostStatusUpdate(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProjectStatusUpdateRequest) =>
      http.post<ProjectStatusUpdate>(`/projects/${projectId}/status-updates`, input),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.projectStatusUpdates(projectId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.projects }),
      ]);
      toast.success('Status posted — followers have been notified');
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

export function useFollowProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, following }: { id: string; following: boolean }) =>
      following
        ? http.put<Project>(`/projects/${id}/follow`)
        : http.delete<Project>(`/projects/${id}/follow`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.projects });
    },
    onError: (error: Error) => toast.error(error.message),
  });
}
